import { useCan } from '@/components/can/Can';
import { ImageCropModal } from '@/components/cropper/ImageCropModal';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import PasswordInput from '@/components/forms/PasswordInput';
import { SwitchInput } from '@/components/forms/Switch';
import { InputGroupLayout } from '@/components/layout/FormGroupLayout';
import { Separator } from '@/design-system/separator';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { optionalUploadValueSchema } from '@/lib/uploadSchema';
import { FileObject, uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { userProfile, userProfilePayload } from '@/services/userProfile/userProfile.types';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from 'ui/button';
import { z } from 'zod';
import useUpdateUserProfile from '../hooks/useUpdateUserProfile';

const passwordComplexityRegex = {
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  number: /[0-9]/,
  symbol: /[^A-Za-z0-9]/,
};

const profileFormSchema = z
  .object({
    username: z.string().min(2, { message: 'common.form.validation.required' }),
    profileName: z.string().min(1, { message: 'common.form.validation.required' }),
    roleName: z.string().min(1, { message: 'common.form.validation.required' }),
    currentPassword: z.string().optional().or(z.literal('')),
    newPassword: z.string().optional().or(z.literal('')),
    confirmPassword: z.string().optional().or(z.literal('')),
    profileImage: optionalUploadValueSchema,
    stayLoggedOutAll: z.boolean(),
  })
  .superRefine((data, ctx) => {
    const hasCurrent = Boolean(data.currentPassword);
    const hasNew = Boolean(data.newPassword);
    const hasConfirm = Boolean(data.confirmPassword);
    const isChanging = hasCurrent || hasNew || hasConfirm;

    if (isChanging) {
      const newPassword = data.newPassword ?? '';
      if (!hasCurrent) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['currentPassword'],
          message: 'common.form.validation.currentPasswordRequired',
        });
      }
      if (!hasNew) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['newPassword'],
          message: 'common.form.validation.newPasswordRequired',
        });
      } else {
        if (newPassword.length < 8) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['newPassword'],
            message: 'common.form.validation.passwordMinLength',
          });
        }
        if (newPassword.length > 32) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['newPassword'],
            message: 'common.form.validation.passwordMaxLength',
          });
        }
        if (!passwordComplexityRegex.uppercase.test(newPassword)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['newPassword'],
            message: 'common.form.validation.passwordUppercaseRequired',
          });
        }
        if (!passwordComplexityRegex.lowercase.test(newPassword)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['newPassword'],
            message: 'common.form.validation.passwordLowercaseRequired',
          });
        }
        if (!passwordComplexityRegex.number.test(newPassword)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['newPassword'],
            message: 'common.form.validation.passwordNumberRequired',
          });
        }
        if (!passwordComplexityRegex.symbol.test(newPassword)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['newPassword'],
            message: 'common.form.validation.passwordSymbolRequired',
          });
        }
      }

      if (!hasConfirm) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['confirmPassword'],
          message: 'common.form.validation.confirmPasswordRequired',
        });
      }
      if (hasCurrent && hasNew && data.currentPassword === data.newPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['newPassword'],
          message: 'common.form.validation.currentPasswordMatch',
        });
      }
      if (hasNew && hasConfirm && data.newPassword !== data.confirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['confirmPassword'],
          message: 'common.form.validation.passwordMatch',
        });
      }
    }
  });
export const ProfileForm = ({ userData }: { userData: userProfile }) => {
  const form = useForm({
    schema: profileFormSchema,
    mode: 'onBlur',
    defaultValues: {
      profileName: userData.profileName,
      username: userData.username,
      profileImage: userData.profileImage.path,
      roleName: userData.roleName,
      stayLoggedOutAll: false,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const blockWhitespace = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ') e.preventDefault();
  };
  const [selectedImage, setSelectedImage] = useState<string | null>(userData.profileImage.path ?? null);
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };
  const maxFileSizeMB = 5 * 1024 * 1024;
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      toast.error(t('common.form.validation.invalidFileType'));
      return;
    }

    if (file && file.size > maxFileSizeMB) {
      toast.error(t('common.form.validation.fileSizeExceeds5MB'));
      return;
    }
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setCropSource(imageUrl);
      setIsCropOpen(true);
    }
  };
  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: 'currentPassword' | 'newPassword' | 'confirmPassword',
  ) => {
    const cleaned = e.target.value.replace(/\s/g, '');
    form.setValue(fieldName, cleaned, {
      shouldDirty: true,
    });
  };
  const handleDelete = () => {
    setSelectedImage(null);
    form.setValue('profileImage', '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const { t } = useDynamicLocale();
  const canUpdate = useCan({ allOf: [PERMS.account.profile.update] });
  const { mutateAsyncUpdateUserProfile } = useUpdateUserProfile();
  const handleSubmit = async (values: z.infer<typeof profileFormSchema>) => {
    let profileImagePath = '';

    const imageValue = values.profileImage as unknown;
    if (!imageValue) {
      profileImagePath = '';
    } else if (typeof imageValue === 'string') {
      profileImagePath = imageValue;
    } else if (typeof imageValue === 'object' && 'file' in (imageValue as FileObject)) {
      profileImagePath = await uploadFileToS3WithPresignedUrl(imageValue as FileObject);
    }

    const payload = {
      profileName: values.profileName,
      profileImagePath,
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      stayLoggedOutAllDevices: values.stayLoggedOutAll,
    } as unknown as userProfilePayload;

    await mutateAsyncUpdateUserProfile(payload, {
      onSuccess: () => {
        form.reset({
          ...values,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      },
    });
  };

  useEffect(() => {
    return () => {
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage.split('?q=')[0]);
      }
    };
  }, [selectedImage]);

  return (
    <Form form={form} onSubmit={handleSubmit} autoComplete="off">
      <div>
        <div className="flex gap-4 w-full items-center">
          <div className="uppercase text-base font-bold w-[134px] whitespace-nowrap">
            <Translation id="account.profile.form.profileInfo.title" />
          </div>
          <Separator variant="muted" thickness="lg" />
        </div>

        <div className="flex justify-between items-end my-6 gap-2">
          <div className="flex items-end gap-9">
            <img
              key={selectedImage}
              className="rounded-full border aspect-square size-[126px] md:size-[167px]"
              src={selectedImage || '/no-image.jpeg'}
              alt={''}
            />
            <ImageCropModal
              open={isCropOpen}
              imageSrc={cropSource}
              cropShape="round"
              onClose={() => {
                setIsCropOpen(false);

                if (cropSource?.startsWith('blob:')) {
                  URL.revokeObjectURL(cropSource);
                }

                setCropSource(null);
              }}
              onCropDone={(croppedFile, previewUrl) => {
                const croppedFileObject: FileObject = {
                  file: croppedFile,
                  id: `${Date.now()}`,
                  preview: previewUrl,
                };

                if (cropSource?.startsWith('blob:')) {
                  URL.revokeObjectURL(cropSource);
                }

                setSelectedImage(previewUrl);

                form.setValue('profileImage', croppedFileObject, {
                  shouldValidate: true,
                  shouldDirty: true,
                });

                setCropSource(null);
                setIsCropOpen(false);
              }}
            />
            <div className="hidden sm:flex flex-col mb-7.5">
              <div className="text-sm font-bold">
                <Translation id="account.profile.form.profileInfo.profilePicture" />
              </div>
              <div className="text-xs font-normal">
                <Translation id="account.profile.form.profileInfo.acceptsJpgPng" />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-4 sm:mb-7.5">
            <Button variant="secondary" onClick={handleDelete} type="button" disabled={!canUpdate || !selectedImage}>
              <Translation id="account.profile.form.profileInfo.delete" />
            </Button>
            <Button onClick={handleButtonClick} type="button" disabled={!canUpdate}>
              <Translation id="account.profile.form.profileInfo.changePicture" />
            </Button>
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              {...form.register('profileImage')}
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
            />
            {form.formState.errors.profileImage && (
              <p className="text-error-emphasis text-sm">{form.formState.errors.profileImage.message}</p>
            )}
          </div>
        </div>
      </div>
      <div className="w-full flex flex-col">
        <InputGroupLayout direction={'vertical'}>
          <div className="max-w-[322px] min-w-[200px]">
            <Input
              type="text"
              label={'account.profile.form.profileInfo.profileName'}
              {...form.register('profileName')}
              placeholder={t('common.form.placeholder.profileName')}
              maxLength={50}
              disabled={!canUpdate}
            />
          </div>
          <InputGroupLayout direction={'horizontal'} className="flex-wrap sm:flex-nowrap ">
            <div className="max-w-[322px] w-full ">
              <Input
                label="account.profile.form.profileInfo.userName"
                {...form.register('username')}
                maxLength={50}
                disabled
              />
            </div>
            <div className="max-w-[322px]  w-full">
              <Input
                label="account.profile.form.profileInfo.roleName"
                {...form.register('roleName')}
                maxLength={50}
                disabled
              />
            </div>
          </InputGroupLayout>
        </InputGroupLayout>

        <div className="flex gap-4 w-full items-center mt-8 mb-6">
          <span className="uppercase text-copy font-bold whitespace-nowrap">
            <Translation id="account.profile.form.profileInfo.password" />
          </span>
          <Separator variant="muted" thickness="lg" />
        </div>
        <InputGroupLayout direction={'vertical'}>
          <div className="max-w-[322px]">
            <PasswordInput
              label="account.profile.form.profileInfo.currentPassword"
              {...form.register('currentPassword')}
              onChange={(e) => handlePasswordChange(e, 'currentPassword')}
              autoComplete="new-password"
              onKeyDown={blockWhitespace}
              placeholder={t('common.form.placeholder.currentPassword')}
              disabled={!canUpdate}
            />
          </div>
          <InputGroupLayout direction={'horizontal'} className="flex-wrap sm:flex-nowrap ">
            <div className="max-w-[322px] w-full">
              <PasswordInput
                label="account.profile.form.profileInfo.newPassword"
                {...form.register('newPassword')}
                onChange={(e) => handlePasswordChange(e, 'newPassword')}
                autoComplete="new-password"
                onKeyDown={blockWhitespace}
                placeholder={t('common.form.placeholder.newPassword')}
                disabled={!canUpdate}
              />
            </div>
            <div className="max-w-[322px]  w-full">
              <PasswordInput
                label="account.profile.form.profileInfo.confirmNewPassword"
                {...form.register('confirmPassword')}
                onChange={(e) => handlePasswordChange(e, 'confirmPassword')}
                autoComplete="new-password"
                onKeyDown={blockWhitespace}
                placeholder={t('common.form.placeholder.confirmPassword')}
                disabled={!canUpdate}
              />
            </div>
          </InputGroupLayout>
        </InputGroupLayout>
      </div>

      <div className="flex flex-col gap-4 mt-6">
        <p className="text-body font-bold">
          <Translation id="account.profile.form.profileInfo.stayLoggedOut" />
        </p>
        <div className="flex gap-3 items-center">
          <SwitchInput
            label="account.profile.form.profileInfo.allDevice"
            className="data-[state=checked]:bg-success"
            {...form.register('stayLoggedOutAll')}
            disabled={!canUpdate}
          />
        </div>
      </div>

      <Separator className="mt-8 mb-6" variant="muted" thickness="lg" />

      <div className="flex w-full justify-end ">
        <Button type="submit" disabled={!canUpdate || !form.formState.isDirty || form.formState.isSubmitting}>
          {t('common.page.button.update')}
        </Button>
      </div>
    </Form>
  );
};
